// The video page's URL parameters (Seán, 2026-10-21), as on YouTube, for linking to a moment
// of a long talk:
//   t        where to start (75, 1:15, 1m15s, 1h02m15s; a fraction too, 75.32 or 1m15.32s); the
//            address bar follows playback each second (whole seconds), so a bookmark or a copied
//            address lands on the moment watched
//   end      a clip's end (with t its start): the video pauses there, once
//   cc       the captions' language (fr, de…) or off; English when absent
//   version  a two-version talk's: slides or speaker (Bespoke.vue, VersionSwitch.vue)
// Where each video was left is also kept in this browser (2026-10-22, Seán): a talk opened
// again without t, in another tab or another day, carries on from there. Watched to its end,
// it's forgotten and t goes, so play starts it again from the beginning.
// "?t=", not "#t=" as on Vimeo: a hash on the video page shows the thumbnail instead of the
// player. The address changes in place: no history entry, no navigation
import {useLocalStorage} from "@vueuse/core";

const UNITS = {h: 3600, m: 60, s: 1};
// seconds between updates of t while playing (2026-10-22, Seán: each second; replaceState is
// cheap, and browsers only throttle it at hundreds of calls a minute)
const TIME_STEP = 1;
const DEFAULT_CAPTIONS = "en";
const positions = useLocalStorage("video-positions", {}); // {<the page's hash>: seconds}

export function getSeconds(value) {
  const text = String(value ?? "").trim();
  if (/^\d+(\.\d+)?$/.test(text)) return Number(text);
  if (/^\d+(:\d{1,2}){1,2}$/.test(text)) {
    return text.split(":").reduce((total, part) => total * 60 + Number(part), 0);
  }
  const parts = [...text.matchAll(/(\d+(?:\.\d+)?)([hms])/g)];
  return parts.reduce((total, [, number, unit]) => total + Number(number) * UNITS[unit], 0);
}

// 3735 → "1h2m15s", 75 → "1m15s", 9 → "9s"
export function getTimeText(seconds) {
  const whole = Math.floor(seconds);
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const hoursText = hours ? `${hours}h` : "";
  const minutesText = hours || minutes ? `${minutes}m` : "";
  return `${hoursText}${minutesText}${whole % 60}s`;
}

export function getUrlParam(name) {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(name);
}

// null or "" removes a parameter
export function setUrlParams(values) {
  const url = new URL(window.location.href);
  for (const [name, value] of Object.entries(values)) {
    if (value === null || value === undefined || value === "") url.searchParams.delete(name);
    else url.searchParams.set(name, value);
  }
  if (url.href !== window.location.href) window.history.replaceState(window.history.state, "", url);
}

// the player's texttrack option: cc's language, none for "off", else English
export function getCaptionStart() {
  const captions = getUrlParam("cc");
  if (captions === "off") return undefined;
  return captions || DEFAULT_CAPTIONS;
}

// captions changed in Vimeo's CC menu: their language into cc ("en-x-autogen" → absent, as
// English is the default); "off" only once captions had been on, as a talk without any reports
// none from the start
function getCaptionsListener() {
  let hadCaptions = getUrlParam("cc") === "off";
  return (track) => {
    const language = track?.language?.split("-")[0] || null;
    if (!language && !hadCaptions) return;
    hadCaptions ||= Boolean(language);
    setUrlParams({cc: language === DEFAULT_CAPTIONS ? null : language || "off"});
  };
}

// key: the video's in this browser's positions (none: not kept, e.g. a looping showcase)
function savePosition(key, seconds) {
  if (!key) return;
  const next = {...positions.value};
  if (seconds >= 1) next[key] = Math.floor(seconds);
  else delete next[key];
  positions.value = next;
}

// getKey: the video shown now (the page can change video in the same player)
function getTimeListeners(player, getKey) {
  let clipEnd = getSeconds(getUrlParam("end"));
  let lastStep = -1;
  let isComplete = false; // watched to the end: nothing kept until it plays again
  function writeTime(seconds) {
    if (isComplete) return;
    const end = clipEnd ? getTimeText(clipEnd) : null;
    setUrlParams({t: seconds >= 1 ? getTimeText(seconds) : null, end});
    savePosition(getKey(), seconds);
  }
  function complete() {
    writeTime(0);
    isComplete = true;
  }
  function endClip(seconds) {
    clipEnd = 0;
    player.pause().catch(() => {});
    writeTime(seconds);
  }
  function onTimeUpdate({seconds}) {
    if (clipEnd && seconds >= clipEnd) return endClip(seconds);
    const step = Math.floor(seconds / TIME_STEP);
    if (step === lastStep) return;
    lastStep = step;
    writeTime(seconds);
  }
  function onSeeked({seconds}) {
    if (clipEnd && seconds > clipEnd) clipEnd = 0; // sought past the clip: the rest plays on
    writeTime(seconds);
  }
  return {timeupdate: onTimeUpdate, seeked: onSeeked, pause: onSeeked, ended: complete,
    play: () => (isComplete = false)};
}

// once the player is ready: start at t, else where this browser left it, then follow playback
// and the captions; getKey: the video's key (the page's hash), "" to keep no position
export function followVideoUrl(player, getKey = () => "") {
  const key = getKey();
  const startAt = getSeconds(getUrlParam("t")) || (key && positions.value[key]) || 0;
  if (startAt) player.setCurrentTime(startAt).catch(() => {});
  const listeners = {...getTimeListeners(player, getKey), texttrackchange: getCaptionsListener()};
  for (const [name, listener] of Object.entries(listeners)) player.on(name, listener);
}
